import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertDescription,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter
} from '@chakra-ui/react';
import { 
  AddIcon, 
  SearchIcon, 
  ChevronDownIcon, 
  EditIcon, 
  DeleteIcon, 
  ViewIcon,
  TriangleUpIcon as PlayIcon,
  TriangleDownIcon as StopIcon,
} from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const ABTesting = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_tests: 0,
    running_tests: 0,
    completed_tests: 0,
    draft_tests: 0,
    avg_conversion_rate: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTest, setSelectedTest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (tenantId) {
      fetchTests();
      fetchStats();
    }
  }, [tenantId]);

  const fetchTests = async () => {
    try {
      setLoading(true);
      console.log('Fetching AB tests for tenant:', tenantId);
      
      const response = await apiClient(`/ab-tests/${tenantId}`);
      console.log('AB tests response:', response);
      
      if (response && response.success && response.data) {
        setTests(response.data);
      } else if (Array.isArray(response)) {
        setTests(response);
      } else {
        setTests([]);
      }
    } catch (error) {
      console.error('Error fetching AB tests:', error);
      toast({
        title: 'Error loading tests',
        description: error.message || 'Failed to load A/B tests',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      console.log('Fetching AB test stats for tenant:', tenantId);
      
      const response = await apiClient(`/ab-tests/${tenantId}/stats`);
      console.log('AB test stats response:', response);
      
      if (response && response.success && response.data) {
        setStats(response.data);
      } else if (response && typeof response === 'object') {
        setStats(response);
      }
    } catch (error) {
      console.error('Error fetching AB test stats:', error);
      // Don't show error toast for stats, just keep defaults
    }
  };

  const handleStartTest = async (testId) => {
    try {
      setActionLoading(true);
      console.log('Starting test:', testId);
      
      const response = await apiClient(`/ab-tests/${tenantId}/${testId}/start`, {
        method: 'POST'
      });
      
      console.log('Start test response:', response);
      
      if (response && response.success) {
        toast({
          title: 'Test started',
          description: response.message || 'A/B test started successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        fetchTests();
        fetchStats();
      } else {
        throw new Error(response?.message || 'Failed to start test');
      }
    } catch (error) {
      console.error('Error starting test:', error);
      toast({
        title: 'Error starting test',
        description: error.message || 'Failed to start A/B test',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopTest = async (testId) => {
    try {
      setActionLoading(true);
      console.log('Stopping test:', testId);
      
      const response = await apiClient(`/ab-tests/${tenantId}/${testId}/stop`, {
        method: 'POST'
      });
      
      console.log('Stop test response:', response);
      
      if (response && response.success) {
        toast({
          title: 'Test stopped',
          description: response.message || 'A/B test stopped successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        fetchTests();
        fetchStats();
      } else {
        throw new Error(response?.message || 'Failed to stop test');
      }
    } catch (error) {
      console.error('Error stopping test:', error);
      toast({
        title: 'Error stopping test',
        description: error.message || 'Failed to stop A/B test',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    try {
      setActionLoading(true);
      console.log('Deleting test:', testId);
      
      const response = await apiClient(`/ab-tests/${tenantId}/${testId}`, {
        method: 'DELETE'
      });
      
      console.log('Delete test response:', response);
      
      if (response && response.success) {
        toast({
          title: 'Test deleted',
          description: response.message || 'A/B test deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        fetchTests();
        fetchStats();
      } else {
        throw new Error(response?.message || 'Failed to delete test');
      }
    } catch (error) {
      console.error('Error deleting test:', error);
      toast({
        title: 'Error deleting test',
        description: error.message || 'Failed to delete A/B test',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
      onClose();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'green';
      case 'completed': return 'blue';
      case 'paused': return 'orange';
      default: return 'gray';
    }
  };

  const getEntityTypeLabel = (entityType) => {
    switch (entityType) {
      case 'component': return 'Component Layout';
      case 'content_entry': return 'Content Entry';
      case 'navigation': return 'Navigation';
      default: return entityType;
    }
  };

  const formatPercent = (value) => {
    return `${parseFloat(value || 0).toFixed(1)}%`;
  };

  const formatNumber = (value) => {
    return parseInt(value || 0).toLocaleString();
  };

  const filteredTests = tests.filter(test => {
    const matchesSearch = test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         test.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || test.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Container maxW="container.xl" py={6}>
        <Center py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Loading A/B tests...</Text>
          </VStack>
        </Center>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Box>
            <Heading size="lg" mb={2}>A/B Testing</Heading>
            <Text color="gray.500">
              Create and manage A/B tests to optimize your content
            </Text>
          </Box>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => navigate('/ab-testing/create')}
          >
            Create Test
          </Button>
        </HStack>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Tests</StatLabel>
                <StatNumber>{stats.total_tests}</StatNumber>
                <StatHelpText>All time</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Running Tests</StatLabel>
                <StatNumber color="green.500">{stats.running_tests}</StatNumber>
                <StatHelpText>Currently active</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Completed Tests</StatLabel>
                <StatNumber color="blue.500">{stats.completed_tests}</StatNumber>
                <StatHelpText>Finished</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Avg. Conversion Rate</StatLabel>
                <StatNumber>{formatPercent(stats.avg_conversion_rate)}</StatNumber>
                <StatHelpText>Across all tests</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Filters */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <HStack spacing={4}>
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search tests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              
              <Select
                placeholder="All statuses"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                maxW="200px"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="running">Running</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </Select>
            </HStack>
          </CardBody>
        </Card>

        {/* Tests Table */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Your A/B Tests</Heading>
          </CardHeader>
          <CardBody>
            {filteredTests.length === 0 ? (
              <Center py={10}>
                <VStack spacing={4}>
                  <Text color="gray.500">No A/B tests found</Text>
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="blue"
                    onClick={() => navigate('/ab-testing/create')}
                  >
                    Create Your First Test
                  </Button>
                </VStack>
              </Center>
            ) : (
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Test Name</Th>
                      <Th>Type</Th>
                      <Th>Status</Th>
                      <Th>Impressions</Th>
                      <Th>Conversion Rate</Th>
                      <Th>Improvement</Th>
                      <Th>Created</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredTests.map((test) => (
                      <Tr key={test.id}>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">{test.name}</Text>
                            {test.description && (
                              <Text fontSize="sm" color="gray.500" noOfLines={1}>
                                {test.description}
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>{getEntityTypeLabel(test.entity_type)}</Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(test.status)}>
                            {test.status}
                          </Badge>
                        </Td>
                        <Td>
                          {formatNumber((test.impressions_a || 0) + (test.impressions_b || 0))}
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text>A: {formatPercent(test.conversion_rate_a)}</Text>
                            <Text>B: {formatPercent(test.conversion_rate_b)}</Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Text color={
                            test.improvement > 0 ? 'green.500' : 
                            test.improvement < 0 ? 'red.500' : 'gray.500'
                          }>
                            {test.improvement > 0 && '+'}
                            {formatPercent(test.improvement)}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {new Date(test.created_at).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<ChevronDownIcon />}
                              variant="ghost"
                              size="sm"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<ViewIcon />}
                                onClick={() => navigate(`/ab-testing/results/${test.id}`)}
                              >
                                View Results
                              </MenuItem>
                              <MenuItem
                                icon={<EditIcon />}
                                onClick={() => navigate(`/ab-testing/edit/${test.id}`)}
                              >
                                Edit Test
                              </MenuItem>
                              {test.status === 'draft' && (
                                <MenuItem
                                  icon={<PlayIcon />}
                                  onClick={() => handleStartTest(test.id)}
                                  isDisabled={actionLoading}
                                >
                                  Start Test
                                </MenuItem>
                              )}
                              {test.status === 'running' && (
                                <MenuItem
                                  icon={<StopIcon />}
                                  onClick={() => handleStopTest(test.id)}
                                  isDisabled={actionLoading}
                                >
                                  Stop Test
                                </MenuItem>
                              )}
                              <MenuItem
                                icon={<DeleteIcon />}
                                onClick={() => {
                                  setSelectedTest(test);
                                  onOpen();
                                }}
                                color="red.500"
                                isDisabled={test.status === 'running'}
                              >
                                Delete Test
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Delete Confirmation Modal */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={undefined}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete A/B Test
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{selectedTest?.name}"? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => handleDeleteTest(selectedTest?.id)}
                ml={3}
                isLoading={actionLoading}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default ABTesting;